import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { IOccurrence } from "interfaces/Occurrence";
import { IGetByReport } from "interfaces/Report";
import { getReport } from "../services/ReportServices";
import { socket } from "hooks/useSocket";

type ProviderValues = {
  children: ReactNode;
};

type ContextValues = {
  report: IGetByReport | undefined;
  setReport: React.Dispatch<React.SetStateAction<IGetByReport | undefined>>;
  sending: boolean;
  setSending: React.Dispatch<React.SetStateAction<boolean>>;
  searching: boolean;
  resetReport: () => void;
  retrieveReport: (
    occurrence: IOccurrence
  ) => Promise<IGetByReport | undefined>;
};

export const ReportContext = createContext({} as ContextValues);

export function ReportProvider({ children }: ProviderValues) {
  const [report, setReport] = useState<IGetByReport>();
  const [sending, setSending] = useState(false);
  const [searching, setSearching] = useState<boolean>(false);

  useEffect(() => {
    if (report)
      socket.on(`attachment-update-${report.id}`, ({ id }: { id: string }) => {
        retrieveReport({
          ...report.occurrence,
          report: [{ id }],
        } as IOccurrence);
      });
  }, []);

  async function retrieveReport(occurrenceData: IOccurrence) {
    setSearching(true);

    if (
      occurrenceData.report &&
      occurrenceData.report[0] &&
      occurrenceData.report[0].id
    ) {
      return await getReport(occurrenceData.report[0].id)
        .then((res) => {
          setReport({ ...res.data.item });
          setSearching(false);

          return { ...res.data.item };
        })
        .catch((err) => {
          setReport({} as IGetByReport);
          setSearching(false);

          return undefined;
        });
    }
    setReport({} as IGetByReport);
    setSearching(false);

    return undefined;
  }

  function resetReport() {
    setSearching(true);
    setReport(undefined);
    setSearching(false);
  }

  return (
    <ReportContext.Provider
      value={{
        report,
        setReport,
        resetReport,
        retrieveReport,
        searching,
        sending,
        setSending,
      }}
    >
      {children}
    </ReportContext.Provider>
  );
}

export const useReport = () => {
  return useContext(ReportContext);
};
